import {
  ApolloLink,
  InMemoryCache,
  Observable,
  HttpLink,
} from "@apollo/client";
import {
  CombinedGraphQLErrors,
  ServerError,
  CombinedProtocolErrors,
  ServerParseError,
} from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { Cookies } from "react-cookie";
import { ErrorLink } from "@apollo/client/link/error";
import * as jose from "jose";
import {
  createApolloLoaderHandler,
  ApolloClient,
} from "@apollo/client-integration-react-router";
import { Defer20220824Handler } from "@apollo/client/incremental";
import * as Sentry from "@sentry/react";
import {
  buildConnectionAuth,
  classifyRefreshFailure,
  classifyRefreshResponse,
  createSubscriptionAuthRecovery,
  RefreshRejection,
  type RefreshOutcome,
  type RecoveryAttemptContext,
  type SubscriptionAuthRecovery,
} from "~/graphql/auth-recovery";
import {
  createBrowserConnectivityDiagnostics,
  createDiagnosticHttpLink,
  refreshFailureCategory,
  type RefreshDiagnosticContext,
  type RefreshDiagnosticSnapshot,
} from "~/graphql/browser-connectivity-diagnostics";
import {
  combinedGraphQLErrorCodes,
  isSubscriptionDocument,
  extractUnauthorizedFromError,
  graphQLErrorFingerprint,
  shouldRecoverSubscriptionAuth,
  shouldReportCombinedGraphQLErrors,
} from "~/graphql/error-detection";
import {
  onWsReconnectStuck,
  reportWsConnectivity,
} from "~/graphql/connectivity";
import {
  createReauthStreak,
  isFatalWsCloseCode,
  isReauthClose,
  shouldRetryWsFailure,
  wsRetryWait,
} from "~/graphql/ws-retry";
import { browserHttpGraphqlUrl } from "~/graphql/http-transport";
import { logoutRedirectFor } from "~/utils/logout-url";
import {
  onApiConnectivityObservation,
  onApiUnreachableEpisode,
} from "~/graphql/api-connectivity";
import {
  noteUnreachableIfSo,
  reachabilityLink,
} from "~/graphql/api-connectivity-link";

/**
 * GraphQL Client Configuration
 *
 * This file configures Apollo Client with WebSocket support for subscriptions.
 *
 * Reconnection Strategy (see `~/graphql/ws-retry.ts` for the policy itself):
 * - WebSocket connections can fail when the computer sleeps or the network changes
 * - graphql-ws retries with infinite attempts and a capped, jittered backoff;
 *   `shouldRetry` covers the browser's `error` event as well as close events
 *   (the library default only retried close events — WEB-SPACE-1WH)
 * - Active subscriptions automatically resubscribe after reconnection, with the
 *   same operation ids, and `connectionParams` re-runs so a refreshed token is
 *   attached. The server asks for that explicitly by closing with 4403 when the
 *   token captured at connection_init has expired.
 * - Individual drops are breadcrumbs; only a tab that cannot reconnect after
 *   several attempts, or a fatal close code, is reported to Sentry.
 */

const cookies = new Cookies();

let graphqlUrl = "";
let revision = "";
if (typeof window === "undefined") {
  // SSR (in-container) prefers a loopback URL pointing at the Hono backend
  // running in the same Cloud Run instance, skipping Cloudflare + GCP LB +
  // dispatcher hops. Falls back to the public URL when GRAPHQL_API_LOCALHOST
  // is unset (local dev, where the localhost var is not configured).
  graphqlUrl =
    (process.env.GRAPHQL_API_LOCALHOST as string | undefined) ??
    (process.env.GRAPHQL_API as string);
  revision = process.env.REVISION as string;
} else {
  graphqlUrl = browserHttpGraphqlUrl(
    {
      environment: webConfig.environment,
      graphqlAPI: webConfig.graphqlAPI,
    },
    window.location,
  );
  revision = webConfig.revision;
}

const browserDiagnostics =
  typeof window === "undefined"
    ? undefined
    : createBrowserConnectivityDiagnostics({
        sink: {
          captureMessage: (message, context) =>
            Sentry.captureMessage(message, context),
          addBreadcrumb: (breadcrumb) => Sentry.addBreadcrumb(breadcrumb),
        },
      });

const httpLink = browserDiagnostics
  ? createDiagnosticHttpLink({ uri: graphqlUrl, diagnostics: browserDiagnostics })
  : new HttpLink({ uri: graphqlUrl, credentials: "omit" });

// Holds the live graphql-ws client so the error handler can force a
// reconnect (which re-runs `connectionParams` with a fresh token and
// resubscribes every active subscription). Only set in the browser.
let subscriptionAuthRecovery: SubscriptionAuthRecovery | undefined;
// Re-auth (4403) closes in a row. Deliberately NOT reset on `connected`: the
// server's 4403 is sent per Subscribe, i.e. always after a connection_ack,
// so a counter reset on connect could never see a loop (see ws-retry.ts).
const reauthStreak = createReauthStreak();

let endpointLink = httpLink;
if (typeof window !== "undefined") {
  const wsClient = createClient({
    url: webConfig.graphqlAPI.replaceAll("https:", "wss:"),
    keepAlive: 30000,
    // A socket that completes the handshake but never acks (captive portal,
    // a proxy buffering the upgrade) must not hang forever: 4504 is retried.
    connectionAckWaitTimeout: 10_000,
    retryAttempts: Infinity,
    shouldRetry: shouldRetryWsFailure,
    retryWait: (retries) => {
      // graphql-ws resets `retries` on every ack; a credential the server
      // keeps rejecting therefore backs off on the re-auth streak instead.
      const waitMs = wsRetryWait(
        Math.max(retries, reauthStreak.backoffLevel()),
      );
      return new Promise((resolve) => setTimeout(resolve, waitMs));
    },
    connectionParams: async () => {
      // Refresh the token if it's close to expiry, then attach it. If the
      // refresh throws (transient network/5xx) we deliberately fall back to
      // the existing cookie token instead of connecting anonymously — going
      // anonymous on a blip would guarantee an `unauthorized.user` rejection.
      let refreshedToken: string | undefined;
      try {
        refreshedToken =
          (await refreshIfCloseToExpiration(cookies, undefined, {
            source: "ws_connection",
          })) ?? undefined;
      } catch (error) {
        console.error("[WS] Error refreshing token:", error);
      }
      return buildConnectionAuth({
        refreshedToken,
        cookieToken: cookies.get("x-ms-access-token"),
      });
    },
    on: {
      closed: (event) => {
        const closeEvent = event as CloseEvent;
        const { code, reason } = closeEvent;

        reportWsConnectivity("closed");

        if (code === 1000) {
          return;
        }
        if (code === 1001) {
          console.warn("[WS] WebSocket closed (1001 - Going Away). Ignoring.");
          return;
        }

        if (isReauthClose(code)) {
          // The token captured at connection_init aged out. graphql-ws is
          // already reconnecting, and `connectionParams` refreshes an expired
          // token on the way — so the first 4403 needs nothing from us. A
          // second one within the streak window means the token we hold is
          // unusable for another reason (revoked, clock skew): the retry
          // backs off on the streak (see retryWait) and the auth recovery
          // refreshes through the backend or, if that is refused, sends the
          // learner to log in instead of looping.
          const streak = reauthStreak.record();
          console.warn(
            `[WS] Server asked for re-authentication (${code}); reconnecting with a fresh token (streak ${streak})`,
          );
          if (streak >= 2) {
            void subscriptionAuthRecovery?.handleUnauthorized({
              kind: "ws_close",
              closeCode: code,
              streak,
            });
          }
          return;
        }

        Sentry.addBreadcrumb({
          category: "graphql-ws",
          level: "warning",
          message: `WebSocket closed (${code}${reason ? ` ${reason}` : ""})`,
        });

        if (isFatalWsCloseCode(code)) {
          // graphql-ws will not retry these; report once per socket, not once
          // per subscription that dies with it.
          console.error(`[WS] Fatal closure. Code: ${code}, Reason: ${reason}`);
          Sentry.captureMessage(`graphql-ws: fatal close ${code}`, {
            level: "error",
            fingerprint: ["graphql-ws-fatal-close", String(code)],
            tags: { closeCode: String(code) },
          });
          return;
        }

        console.warn(`[WS] WebSocket closed (${code}); retrying.`);
      },
      error: (event) => {
        // The browser's "fail the WebSocket connection" path (network drop,
        // proxy reset, sleep/wake). Retried by graphql-ws; a breadcrumb only.
        Sentry.addBreadcrumb({
          category: "graphql-ws",
          level: "warning",
          message: "WebSocket error event; retrying",
        });
        console.warn("[WS] WebSocket error event; retrying.", event);
      },
      connecting: () => {
        reportWsConnectivity("connecting");
        console.log("[WS] Connecting to GraphQL WebSocket...");
      },
      connected: () => {
        reportWsConnectivity("connected");
        console.log("[WS] Successfully connected to GraphQL WebSocket");
      },
    },
  });
  const wsLink = new GraphQLWsLink(wsClient);

  // The one outage signal: a tab that cannot get back to `connected`. Fires
  // once per outage (re-arms on the next connect), one stable Sentry group.
  onWsReconnectStuck((attempts) => {
    console.error(`[WS] Unable to reconnect after ${attempts} attempts`);
    Sentry.captureMessage("graphql-ws: unable to reconnect", {
      level: "warning",
      fingerprint: ["graphql-ws-reconnect-stuck"],
      tags: { attempts: String(attempts) },
    });
  });

  // HTTP reachability: one warning per unreachable episode (the learner's
  // network, almost always), not one error per failed request.
  onApiUnreachableEpisode((operationName, reason) => {
    console.warn(
      `[GraphQL] API unreachable (${reason}; first failure: ${operationName})`,
    );
    Sentry.captureMessage("graphql: API unreachable", {
      level: "warning",
      fingerprint: ["graphql-network-unreachable", reason],
      tags: { operationName, reason },
      contexts: {
        browserConnectivity: browserDiagnostics?.outageWarningContext() ?? {},
      },
    });
  });
  onApiConnectivityObservation((observation) => {
    browserDiagnostics?.observeConnectivity(observation);
  });

  // Recovery coordinator for mid-subscription auth expiry. The error link
  // (below) calls `handleUnauthorized()` when a subscription frame comes back
  // with `unauthorized.user`; this refreshes the token and forces the socket to
  // reconnect so the subscription resubscribes authenticated. See
  // `auth-recovery.ts` for the debounce / backoff / fail-open rules.
  subscriptionAuthRecovery = createSubscriptionAuthRecovery({
    refresh: attemptSubscriptionRefresh,
    reconnect: () => {
      console.warn("[WS] Forcing reconnect to recover subscription auth");
      // `terminate()` drops the socket without a clean close, which trips the
      // retry logic above and re-runs `connectionParams` with the fresh token.
      wsClient.terminate();
    },
    redirectToLogin,
    createAttempt: (trigger) =>
      browserDiagnostics?.createRecoveryAttempt(trigger) ?? {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- machine-only fallback
        attemptId: "attempt-0",
        trigger,
      },
    observeDecision: (observation) =>
      browserDiagnostics?.observeRecoveryDecision(observation),
  });

  endpointLink = ApolloLink.split(
    ({ query }) => {
      if (typeof window === "undefined") {
        return false;
      }
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink,
  );
}

const refreshTokenLink = new ApolloLink((operation, forward) => {
  if (typeof window !== "undefined") {
    return new Observable((observer) => {
      refreshIfCloseToExpiration(cookies, undefined, {
        source: "http_auth",
        operationName: operation.operationName,
      })
        .then((token) => {
          // Store the refreshed token in operation context
          operation.setContext(({ headers = {} }) => ({
            headers,
            refreshedToken: token,
          }));
        })
        .catch((error) => {
          // This user is probably not authenticated, continue without auth.
          console.warn(
            "[Auth] Token refresh failed, continuing unauthenticated:",
            error,
          );
        })
        .finally(() => {
          // Forward the operation after token refresh attempt
          const subscription = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });

          // Return cleanup function
          return () => subscription.unsubscribe();
        });
    });
  }
  return forward(operation);
});

const metadataHeadersLink = setContext((_, { headers }) => {
  let newHeaders = {
    ...headers,
    "x-nspr-app": "web",
    "x-nspr-app-version": revision,
  };
  return { headers: newHeaders };
});

const authLink = setContext((operation, context) => {
  if (typeof window === "undefined") {
    // In the backend, headers are added in the query directly, but let's add the app/revision
    return {
      headers: {
        ...context.headers,
      },
    };
  }

  // Get the refreshed token from context if available
  const token =
    context.refreshedToken || cookies.get("x-ms-access-token") || "";

  return {
    headers: {
      ...context.headers,
      Authorization: "Bearer " + token,
    },
  };
});

// Probes use the live HTTP auth path, independently of Apollo (no operation replay).
// Read the cookie after refresh on every attempt; never capture a failed request's token.
async function reachabilityHeaders(
  signal: AbortSignal,
): Promise<Record<string, string>> {
  let refreshedToken: string | undefined;
  try {
    refreshedToken = await refreshIfCloseToExpiration(cookies, signal, {
      source: "reachability_probe",
      operationName: "ApiReachability",
    });
  } catch {
    // Match the HTTP auth link's fallback when refresh is temporarily unavailable.
  }
  return {
    Authorization:
      "Bearer " + (refreshedToken || cookies.get("x-ms-access-token") || ""),
    "x-nspr-app": "web",
    "x-nspr-app-version": revision,
  };
}

const errorLink = new ErrorLink(({ error, operation }) => {
  // GraphQL errors from your API
  if (CombinedGraphQLErrors.is(error)) {
    const errorCodes = combinedGraphQLErrorCodes(error);

    error.errors.forEach(({ message, locations, path, extensions }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );

      // Log extensions for debugging
      if (extensions) {
        console.log("[GraphQL error extensions]:", extensions);
      }
    });

    if (errorCodes.includes("internal_service.platform_principal_unverified")) {
      // The platform could not verify our credential (auth service or
      // Secret Manager outage): one stable group, not one exception per
      // operation per tab.
      Sentry.captureMessage("graphql: credential could not be verified", {
        level: "warning",
        fingerprint: ["graphql-auth-service-unverified"],
        tags: { operationName: operation.operationName ?? "unknown" },
      });
    }

    // This response-level gate suppresses expected auth outcomes globally, but
    // suppresses mindspace routing outcomes only for the two root operations
    // redirected by `app/utils/space-routing.ts`. Other operations still report.
    if (shouldReportCombinedGraphQLErrors(operation.operationName, error)) {
      Sentry.captureException(error, {
        fingerprint: graphQLErrorFingerprint(operation.operationName, error),
        tags: {
          errorType: "graphql",
          graphqlErrorCodes: errorCodes.join(","),
        },
        contexts: {
          graphql: {
            operationName: operation.operationName,
            query: operation.query.loc?.source.body,
            variables: operation.variables,
            errorCodes,
          },
        },
      });
    }

    // Subscription auth recovery: if a subscription frame failed with
    // `unauthorized.user`, refresh + force a reconnect instead of leaving the
    // socket permanently broken.
    if (shouldRecoverSubscriptionAuth(error, operation.query)) {
      void subscriptionAuthRecovery?.handleUnauthorized({
        kind: "subscription_graphql",
      });
    }
  }

  // Network/transport errors
  else if (ServerError.is(error)) {
    console.log(
      `[Server error]: ${error.message}, Status: ${error.statusCode}`,
    );
    noteUnreachableIfSo(error, operation, graphqlUrl, reachabilityHeaders);
    Sentry.captureException(error, {
      tags: { errorType: "server" },
    });
  }

  // Subscription protocol errors
  else if (CombinedProtocolErrors.is(error)) {
    console.log(`[Protocol error]: ${error.message}`);
    const isAuthError = extractUnauthorizedFromError(error);
    if (shouldRecoverSubscriptionAuth(error, operation.query)) {
      void subscriptionAuthRecovery?.handleUnauthorized({
        kind: "subscription_protocol",
      });
    }
    // Skip Sentry for expected auth expiry (handled by the recovery above).
    if (!isAuthError) {
      Sentry.captureException(error, {
        tags: { errorType: "protocol" },
      });
    }
  }

  // JSON parsing errors
  else if (ServerParseError.is(error)) {
    console.log(`[Parse error]: ${error.message}`);
    Sentry.captureException(error, {
      tags: { errorType: "parse" },
    });
  }

  // Fallback for unexpected errors
  else if (error) {
    if (error instanceof Error && isSubscriptionDocument(operation.query)) {
      // A subscription's transport failure — GraphQLWsLink's "Socket closed"
      // when graphql-ws gives up on the socket, or a client-side Error (a
      // connectionParams throw, a malformed first frame) that ends in a 4004
      // or 4005 close — is one physical event that the socket-level `closed`
      // handler above already reported. Per-operation copies are breadcrumbs,
      // not another 17 error events per tab.
      Sentry.addBreadcrumb({
        category: "graphql-ws",
        level: "warning",
        message: `${error.message} (${operation.operationName ?? "subscription"})`,
      });
      return;
    }
    if (
      noteUnreachableIfSo(error, operation, graphqlUrl, reachabilityHeaders) ===
      "network"
    ) {
      // The browser never got a response (offline, VPN, proxy, ad blocker).
      // Tell the learner (notice) and count the episode once, not the request.
      Sentry.addBreadcrumb({
        category: "graphql",
        level: "warning",
        message: `API unreachable: ${error.message} (${operation.operationName ?? "operation"})`,
      });
      return;
    }
    console.log(`[Unknown error]:`, error);
    Sentry.captureException(error, {
      tags: { errorType: "unknown" },
    });
  }
});

const directionalLink = new ApolloLink((operation, forward) => {
  return forward(operation);
}).split(
  (operation) => !!operation.getContext().skipAuth,
  metadataHeadersLink
    .concat(errorLink)
    .concat(reachabilityLink)
    .concat(endpointLink),
  metadataHeadersLink
    .concat(errorLink)
    .concat(refreshTokenLink)
    .concat(authLink)
    .concat(reachabilityLink)
    .concat(endpointLink),
);
export const getServerClient = (request?: Request) => {
  return new ApolloClient({
    cache: new InMemoryCache(),

    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all", // Return errors in response instead of throwing
      },
    },

    link: directionalLink,

    /*
    Inserted by Apollo Client 3->4 migration codemod.
    If you are not using the `@defer` directive in your application,
    you can safely remove this option.
    */
    incrementalHandler: new Defer20220824Handler(),
  });
};
export const apolloLoader = createApolloLoaderHandler(getServerClient);

function refreshToken(
  signal?: AbortSignal,
  diagnosticContext?: RefreshDiagnosticContext,
) {
  const diagnostics = diagnosticContext
    ? browserDiagnostics?.startRefresh(diagnosticContext)
    : undefined;
  const finishDiagnostics = (
    result: Parameters<NonNullable<typeof diagnostics>["finish"]>[0],
    classification?: "invalid" | "transient",
  ) => {
    try {
      const snapshot = diagnostics?.finish(result, classification);
      if (snapshot) diagnosticContext?.onFinished?.(snapshot);
    } catch {
      // Diagnostics must not change refresh parsing or rejection identity.
    }
  };
  return fetch("/api/oauth/access_token", { signal })
    .then(async (response) => {
      diagnostics?.noteResponse(response);
      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");
      let data;
      try {
        data = isJson ? await response.json() : null;
      } catch (error) {
        finishDiagnostics("parse_failure", "transient");
        throw error;
      }

      // check for error response
      if (!response.ok) {
        // A Cloudflare challenge answers for our route; the session is fine,
        // and a reload lets the learner pass the check (FOX-6551).
        const challenged =
          response.headers.get("cf-mitigated")?.toLowerCase() === "challenge" ||
          (response.status === 403 && !isJson);
        const error = new RefreshRejection(
          response.status,
          classifyRefreshResponse({
            status: response.status,
            json: !!isJson,
            challenged,
          }),
        );
        finishDiagnostics("http_rejected", error.classification);
        if (challenged) {
          // Loaded on demand: the prompt pulls in the UI kit, which this
          // client otherwise never needs. It is a hint, so a failure to load
          // or show it must not change the refresh outcome.
          import("~/graphql/reload-prompt")
            .then(({ promptReloadAfterChallenge }) => promptReloadAfterChallenge())
            .catch(() => {});
        }
        return Promise.reject(error);
      }

      let accessToken;
      try {
        accessToken = data.AccessToken;
      } catch (error) {
        finishDiagnostics("parse_failure", "transient");
        throw error;
      }
      // Diagnose malformed fields without changing the existing auth result.
      if (typeof accessToken === "string" && accessToken.trim().length > 0) {
        finishDiagnostics("parsed_success");
      } else {
        finishDiagnostics("parse_failure", "transient");
      }
      return Promise.resolve(accessToken);
    })
    .catch((error) => {
      finishDiagnostics(refreshFailureCategory(error), "transient");
      return Promise.reject(error);
    });
}

// Force a token refresh and classify the result for the recovery coordinator.
// Unlike `refreshIfCloseToExpiration`, this always hits the refresh endpoint —
// we only get here once the server has already rejected the current token.
async function attemptSubscriptionRefresh(
  attempt?: RecoveryAttemptContext,
): Promise<RefreshOutcome<RefreshDiagnosticSnapshot>> {
  // No session cookie at all -> there is nothing to refresh; treat as a
  // definitive logout so the user is sent to re-auth rather than looping.
  if (!cookies.get("x-ms-logged-in")) {
    return { ok: false, reason: "invalid" };
  }
  let diagnostic: RefreshDiagnosticSnapshot | undefined;
  try {
    await refreshToken(undefined, {
      source: "subscription_recovery",
      recoveryAttemptId: attempt?.attemptId,
      onFinished: (snapshot) => {
        diagnostic = snapshot;
      },
    });
    return { ok: true, diagnostic };
  } catch (reason) {
    return {
      ok: false,
      reason: classifyRefreshFailure(reason),
      diagnostic,
    };
  }
}

// Send a genuinely logged-out user to login, preserving the current URL so they
// return to the same page (where the subscription replays the latest status).
function redirectToLogin() {
  if (typeof window === "undefined") return;
  window.location.assign(logoutRedirectFor(window.location.href));
}

export async function refreshIfCloseToExpiration(
  cookies: Cookies,
  signal?: AbortSignal,
  diagnosticContext?: RefreshDiagnosticContext,
) {
  let loggedIn = cookies.get("x-ms-logged-in");
  if (!loggedIn) {
    return;
  }
  let tokenValue = cookies.get("x-ms-access-token");
  if (!tokenValue) {
    return refreshToken(signal, diagnosticContext);
  }
  const claims = jose.decodeJwt(tokenValue);
  if (
    claims?.exp &&
    new Date(claims.exp * 1000).getTime() > new Date().getTime() + 20000 // Token still live for 20s at least
  ) {
    return Promise.resolve();
  }
  // It has expired, let's refresh
  return refreshToken(signal, diagnosticContext);
}

/*
Start: Inserted by Apollo Client 3->4 migration codemod.
Copy the contents of this block into a `.d.ts` file in your project to enable correct response types in your custom links.
If you do not use the `@defer` directive in your application, you can safely remove this block.
*/

import "@apollo/client";

declare module "@apollo/client" {
  export interface TypeOverrides extends Defer20220824Handler.TypeOverrides {}
}

/*
End: Inserted by Apollo Client 3->4 migration codemod.
*/
